import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Collapse,
  IconButton,
  Grid,
  Divider,
  TableCell,
  TableRow,
  Table,
  TableHead,
  TableContainer,
  TableBody,
  Paper,
  Box,
  CircularProgress,
  TablePagination,
  Button,
  TextField,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import companyInfo from 'context/companyInfo';
import { UserContext } from 'context/userContext';
import { getAllProductsFromDb } from 'services/product'; // Assuming you can reuse this
import ProductExpandableTable from 'layouts/products/components/ProductExpandableTable/'; // Reuse ProductExpandableTable
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import OfferingDetailsView from './components/OfferingDetailsView'; // Import OfferingDetailsView

function Offering() {
  const { userId } = useContext(UserContext);
  const [productsData, setProductsData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [topLevelPage, setTopLevelPage] = useState(0);
  const [topLevelRowsPerPage, setTopLevelRowsPerPage] = useState(8);
  const [innerPage, setInnerPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [selectedDetailProduct, setSelectedDetailProduct] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (userId) {
          let response = await getAllProductsFromDb(userId);
          if (response?.data?.products) {
            setProductsData(response.data.products);
          } else {
            console.error('Error fetching products:', response?.msg);
          }
        }
      } catch (error) {
        console.error('Failed to fetch product data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const filteredProductsData = useMemo(() => {
    if (!searchTerm) {
      return productsData;
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return productsData.filter((group) => group.groupedBy.name.toLowerCase().includes(lowerCaseSearchTerm));
  }, [productsData, searchTerm]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === rowId ? null : rowId));
    if (expandedRow !== rowId) {
      setInnerPage(0);
    }
  };

  const handleTopLevelPageChange = (event, newPage) => {
    setTopLevelPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setTopLevelPage(0);
  };

  const columns = [
    { name: 'name', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  const startIndex = topLevelPage * topLevelRowsPerPage;
  const endIndex = startIndex + topLevelRowsPerPage;
  const displayedGroups = filteredProductsData.slice(startIndex, endIndex);

  const rows = useMemo(() => {
    return displayedGroups?.map((group) => ({
      id: group.groupedBy.id,
      name: (
        <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
          {group.groupedBy.name}
        </ArgonTypography>
      ),
      onClick: () => handleRowClick(group.groupedBy.id),
      selected: expandedRow === group.groupedBy.id,
      actions: (
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(group.groupedBy.id);
          }}
        >
          <Icon>{expandedRow === group.groupedBy.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
        </IconButton>
      ),
    }));
  }, [displayedGroups, expandedRow]);

  const handleBackClick = () => {
    setShowDetailsView(false);
    setSelectedDetailProduct(null);
  };

  const handleViewDetails = (product) => {
    setSelectedDetailProduct(product);
    setShowDetailsView(true);
  };

  const renderRowSubComponent = ({ row }) => {
    const selectedGroup = productsData.find((group) => group.groupedBy.id === row.id);

    if (!selectedGroup) {
      return null;
    }

    const innerRowsPerPage = 5;
    const tableRows = selectedGroup.products.map((product) => ({
      // Reuse product list from product page
      productName: (
        <ArgonTypography fontWeight="medium" variant="body" color="secondary">
          {product.name || 'Unknown Product'}
        </ArgonTypography>
      ),
      createdDate: (
        <ArgonTypography fontWeight="medium" variant="body" color="secondary">
          {new Date(product.created_date).toLocaleString()}
        </ArgonTypography>
      ),
      createdBy: (
        <ArgonTypography fontWeight="medium" variant="body" color="secondary">
          {product.user.email || 'Unknown User'}
        </ArgonTypography>
      ),
      product_version: (
        <ArgonTypography fontWeight="medium" variant="body" color="secondary">
          {product.product_version || '-'}
        </ArgonTypography>
      ),
      actions: (
        <>
          <Button
            variant="contained"
            color="info"
            size="small"
            style={{ marginRight: '8px' }}
            onClick={() => handleViewDetails(product)}
          >
            View Details
          </Button>
        </>
      ),
      id: product._id,
    }));

    const subColumns = [
      // Reuse sub columns from product page
      {
        name: 'productName',
        label: 'Product Name',
        align: 'left',
        component: (row) => row.productName,
        sx: { fontSize: '10pt', color: grey[500] },
      },
      {
        name: 'createdDate',
        label: 'Created Date',
        align: 'left',
        component: (row) => row.createdDate,
        sx: { fontSize: '10pt', color: grey[500] },
      },
      {
        name: 'createdBy',
        label: 'Created By',
        align: 'left',
        component: (row) => row.createdBy,
        sx: { fontSize: '10pt', color: grey[500] },
      },
      {
        name: 'product_version',
        label: 'Product Version',
        align: 'left',
        component: (row) => row.product_version,
        sx: { fontSize: '10pt', color: grey[500] },
      },
      {
        name: 'actions',
        label: 'Actions',
        align: 'center',
        component: (row) => row.actions,
        sx: { paddingRight: '24px', fontSize: '10pt', color: grey[500] },
      },
    ];

    const handleInnerPageChange = (event, newPage) => {
      setInnerPage(newPage);
    };

    const innerStartIndex = innerPage * innerRowsPerPage;
    const innerEndIndex = innerStartIndex + innerRowsPerPage;
    const displayedInnerRows = tableRows.slice(innerStartIndex, innerEndIndex);

    return (
      <TableRow>
        <TableCell colSpan={columns.length} style={{ padding: 0 }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <ArgonBox p={3} color="text" bgcolor={grey[50]}>
              <TableContainer component={Paper}>
                <Table aria-label="product details table">
                  <TableHead sx={{ display: 'contents' }}>
                    <TableRow>
                      {subColumns.map((column) => (
                        <TableCell key={column.name} align={column.align} sx={column.sx}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedInnerRows.length > 0 ? (
                      displayedInnerRows.map((innerRow) => (
                        <TableRow
                          key={innerRow.id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          {subColumns.map((column) => (
                            <TableCell
                              key={column.name}
                              align={column.align}
                              component={column.name === 'productName' ? 'th' : 'td'}
                              scope={column.name === 'productName' ? 'row' : undefined}
                              sx={column.sx}
                            >
                              {column.component(innerRow)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={subColumns.length}>
                          <ArgonTypography variant="caption" color="textSecondary">
                            No products in this group.
                          </ArgonTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <ArgonBox display="flex" justifyContent="center" mt={2}>
                <Card>
                  <TablePagination
                    rowsPerPageOptions={[innerRowsPerPage]}
                    component="div"
                    sx={{ color: grey[600] }}
                    count={tableRows.length}
                    rowsPerPage={innerRowsPerPage}
                    page={innerPage}
                    onPageChange={handleInnerPageChange}
                    SelectProps={{
                      inputProps: { 'aria-label': 'rows per page' },
                      native: true,
                    }}
                  />
                </Card>
              </ArgonBox>
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3} flexWrap="wrap" gap={2}>
              <ArgonTypography variant="h6">
                {showDetailsView ? selectedDetailProduct.name : 'Offerings'}
              </ArgonTypography>
              <ArgonBox display="flex" alignItems="center" gap={1}>
                {!showDetailsView && (
                  <TextField
                    placeholder="Search Offerings"
                    variant="outlined"
                    size="medium"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ minWidth: '300px', marginRight: '1rem' }}
                    disabled={showDetailsView}
                  />
                )}

                <Button onClick={handleBackClick} variant="contained" color="info" disabled={!showDetailsView}>
                  Back
                </Button>
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                '& .MuiTableRow-root:not(:last-child)': {
                  '& td': {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Card>
                  <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                    <CircularProgress />
                  </Box>
                </Card>
              ) : showDetailsView ? (
                <OfferingDetailsView product={selectedDetailProduct} onClose={handleBackClick} />
              ) : filteredProductsData.length > 0 ? (
                <>
                  <ProductExpandableTable columns={columns} rows={rows} renderRowSubComponent={renderRowSubComponent} />
                  <ArgonBox display="flex" justifyContent="center" mt={2}>
                    <TablePagination
                      rowsPerPageOptions={[topLevelRowsPerPage]}
                      component="div"
                      count={filteredProductsData.length}
                      rowsPerPage={topLevelRowsPerPage}
                      page={topLevelPage}
                      onPageChange={handleTopLevelPageChange}
                      sx={{ color: grey[600] }}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                    />
                  </ArgonBox>
                </>
              ) : (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body1" color="textSecondary">
                    {searchTerm ? 'No offering groups match your search.' : 'No offerings found.'}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <ArgonBox mt={3}>
        <Footer company={companyInfo} links={companyInfo.links} />
      </ArgonBox>
    </DashboardLayout>
  );
}

export default Offering;
